/**
 * api/client.ts — thin fetch wrapper for the AsterWallet backend.
 *
 * Contract (matches api-contract.ts on the mobile side):
 *   * Base URL = `VITE_API_URL` if set, else same-origin `/api`.
 *   * ALWAYS sends `credentials: 'include'` so the HttpOnly
 *     `aw_session` cookie is presented on every request. Without
 *     this, SameSite=Strict cookies would still be BLOCKED by the
 *     browser for XHRs — Strict controls navigation, `credentials`
 *     controls XHR/fetch.
 *   * Automatic error-envelope parsing — errors surface as thrown
 *     `ApiClientError` with `code`, `message`, `retryable` etc.
 *   * CSRF: reads the `aster_csrf` cookie (set by the server on
 *     session mint + refreshed on every authenticated response) and
 *     echoes it in the `X-CSRF-Token` header on state-changing verbs.
 *     The token itself is never stored in JS module state — reading
 *     it from `document.cookie` per-request ensures we always echo
 *     the freshest value the server rotated.
 *
 * NO caching, NO retry logic here — that lives one layer up.
 */

// ---------------------------------------------------------------------------
// Base URL resolution.
// ---------------------------------------------------------------------------
export const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(
  /\/+$/,
  "",
) || "/api";

// ---------------------------------------------------------------------------
// CSRF cookie name — MUST match the server (`auth.web.WEB_CSRF_COOKIE`).
// ---------------------------------------------------------------------------
const CSRF_COOKIE = "aster_csrf";

/**
 * Read the CSRF token from `document.cookie` on demand. Cookies with
 * `Secure; SameSite=Strict` set by the server are same-origin-only,
 * so a cross-origin attacker can NEVER read this value. Reading it
 * per-request (rather than caching in a JS variable) means the server
 * can rotate the token by sending a new Set-Cookie and the very next
 * mutation will pick up the new value automatically.
 */
export function readCsrfFromCookie(): string | null {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie.split(";");
  for (const raw of cookies) {
    const eq = raw.indexOf("=");
    if (eq < 0) continue;
    const key = raw.slice(0, eq).trim();
    if (key === CSRF_COOKIE) {
      return decodeURIComponent(raw.slice(eq + 1).trim());
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Error envelope. Matches api-contract.ts::ApiError.
// ---------------------------------------------------------------------------
export interface ApiErrorEnvelope {
  code: string;
  message: string;
  retryable: boolean;
  field?: string;
  step_up_challenge?: string;
  [k: string]: unknown;
}

export class ApiClientError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly retryable: boolean;
  public readonly envelope: ApiErrorEnvelope;

  constructor(status: number, envelope: ApiErrorEnvelope) {
    super(envelope.message || `HTTP ${status}`);
    this.name = "ApiClientError";
    this.status = status;
    this.code = envelope.code;
    this.retryable = envelope.retryable;
    this.envelope = envelope;
  }

  /**
   * True iff this error means the SPA should show "Signed out for
   * security" rather than a generic auth error. Covers both idle
   * (30-min inactivity) and absolute (8-hour lifetime) web-session
   * expiry — both are non-adversarial events triggered by the
   * server's session-safety policy.
   */
  isSessionExpired(): boolean {
    return (
      this.code === "web_session_idle_expired" ||
      this.code === "web_session_absolute_expired"
    );
  }
}

// ---------------------------------------------------------------------------
// Session-expiry event bus.
//
// The AuthContext subscribes to this once and drops the local session
// state (→ redirects to /signin with a "Signed out for security"
// modal). Any component that catches an ApiClientError can call
// `notifySessionExpired()` to trip the same flow without a direct
// dependency on the auth module — keeps the client layer standalone.
// ---------------------------------------------------------------------------

type ExpiryListener = (code: string, message: string) => void;
const expiryListeners = new Set<ExpiryListener>();

export function onSessionExpired(fn: ExpiryListener): () => void {
  expiryListeners.add(fn);
  return () => {
    expiryListeners.delete(fn);
  };
}

function notifySessionExpired(err: ApiClientError): void {
  for (const fn of expiryListeners) {
    try {
      fn(err.code, err.message);
    } catch {
      // A listener throwing must not break the caller's error flow.
    }
  }
}

// ---------------------------------------------------------------------------
// Options.
// ---------------------------------------------------------------------------
export interface ApiRequestInit {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  /** For endpoints that require device auth (dev sign-in flow only). */
  bearer?: string;
  /** For endpoints that require device auth. Never persisted anywhere. */
  deviceId?: string;
  /** Attach `Idempotency-Key` — required by all product mutation routes. */
  idempotencyKey?: string;
  /** Signal for aborting the request. */
  signal?: AbortSignal;
}

// ---------------------------------------------------------------------------
// Core fetcher.
// ---------------------------------------------------------------------------

export async function api<T = unknown>(
  path: string,
  init: ApiRequestInit = {},
): Promise<T> {
  const method = init.method || "GET";
  const url = `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...init.headers,
  };

  // Bearer + device-id — used ONLY during the dev sign-in flow to
  // exchange for a cookie. Never carried on regular reads.
  if (init.bearer) headers.Authorization = `Bearer ${init.bearer}`;
  if (init.deviceId) headers["X-Device-Id"] = init.deviceId;

  // CSRF: on any state-changing verb, echo the cookie value. We read
  // it per-request from document.cookie so a server-rotated value is
  // picked up immediately. If there's no cookie (e.g., the client is
  // making an anonymous POST like /auth/verify), we skip the header.
  const isMutation = method !== "GET";
  if (isMutation) {
    const csrf = readCsrfFromCookie();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }

  if (init.idempotencyKey) headers["Idempotency-Key"] = init.idempotencyKey;

  let bodyPayload: BodyInit | undefined;
  if (init.body !== undefined) {
    headers["Content-Type"] = "application/json";
    bodyPayload = JSON.stringify(init.body);
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: bodyPayload,
      credentials: "include",
      signal: init.signal,
    });
  } catch (netErr) {
    // Network-level failure — backend unreachable, DNS lookup failure,
    // CORS preflight rejected, aborted, offline. `fetch()` rejects
    // with TypeError("Failed to fetch") or a DOMException("AbortError"),
    // neither of which is a shape any page wants to render directly.
    // Translate to a well-known envelope so callers see one contract.
    //
    // Passing an ApiClientError up gives error handling ONE shape —
    // pages don't need to distinguish "network died" from "500 came
    // back". The `code` is stable ("network_unavailable"), so screens
    // that want to render an offline-specific UI can check for it.
    if (init.signal?.aborted) {
      // Caller cancelled; propagate a canonical abort so React's
      // effect cleanup doesn't produce a "no such error" fallthrough.
      throw new ApiClientError(0, {
        code: "request_cancelled",
        message: "Request cancelled.",
        retryable: false,
      });
    }
    throw new ApiClientError(0, {
      code: "network_unavailable",
      message: "Couldn’t reach AsterCash. Check your connection and try again.",
      retryable: true,
    });
  }

  // 204 No Content — nothing to parse.
  if (response.status === 204) {
    return undefined as T;
  }

  // Try to parse JSON regardless of status; the error envelope IS JSON.
  let json: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      throw new ApiClientError(response.status, {
        code: "internal_error",
        message: _humanCopyFor(response.status),
        retryable: response.status >= 500,
      });
    }
  }

  if (!response.ok) {
    // Two envelope shapes ship in the wild:
    //   * Canonical:  {"error": {"code": "...", "message": "..."}}
    //   * Flat legacy: {"code": "...", "message": "..."}
    // The flat form is what most 401 responses use in this codebase's
    // migration state; a synthesizer that only speaks the nested form
    // would fall through to the fallback envelope and drop the code
    // — which is exactly why the "signed out for security" redirect
    // was missing on a real 401. Accept both.
    const envelope = _extractEnvelope(json, response.status);
    const err = new ApiClientError(response.status, envelope);
    // Fire the session-expiry fan-out on ANY 401. The `isSessionExpired`
    // check is more specific (looks at the code), but SOME 401s come
    // through without a matching code — for those, redirecting to
    // /signin is still the safe default. A 401 while holding a session
    // is by definition a broken session; the shell should not stay
    // authenticated.
    if (response.status === 401 || err.isSessionExpired()) {
      notifySessionExpired(err);
    }
    throw err;
  }

  return json as T;
}

// ---------------------------------------------------------------------------
// Fallback error copy. Never emits raw status codes to the user —
// "HTTP 500" is the kind of thing that erodes trust in a financial
// product ("is this app OK?"). Pages that catch an ApiClientError
// render `.message` directly, so this is where the words live.
// ---------------------------------------------------------------------------

function _humanCopyFor(status: number): string {
  if (status >= 500) {
    return "AsterCash is having trouble. Please try again in a moment.";
  }
  if (status === 401) {
    return "Signed out for security. Sign in again to continue.";
  }
  if (status === 403) {
    return "You don’t have access to that.";
  }
  if (status === 404) {
    return "We couldn’t find that.";
  }
  if (status === 429) {
    return "Too many requests. Try again in a minute.";
  }
  if (status >= 400) {
    return "That request couldn’t be completed.";
  }
  return "Something went wrong.";
}

function _extractEnvelope(json: unknown, status: number): ApiErrorEnvelope {
  if (json && typeof json === "object") {
    const o = json as Record<string, unknown>;
    // Nested canonical shape: {"error": {code, message, retryable?}}
    if (
      "error" in o &&
      typeof o.error === "object" &&
      o.error !== null
    ) {
      const inner = o.error as Record<string, unknown>;
      if (typeof inner.code === "string" && typeof inner.message === "string") {
        return {
          code: inner.code,
          message: inner.message,
          retryable:
            typeof inner.retryable === "boolean"
              ? inner.retryable
              : status >= 500,
        };
      }
    }
    // Flat legacy shape: {code, message, retryable?}
    if (typeof o.code === "string" && typeof o.message === "string") {
      return {
        code: o.code,
        message: o.message,
        retryable:
          typeof o.retryable === "boolean" ? o.retryable : status >= 500,
      };
    }
  }
  // No envelope at all — synthesise a human-copy fallback so pages
  // don't leak "HTTP 500" to the user.
  return {
    code: "internal_error",
    message: _humanCopyFor(status),
    retryable: status >= 500,
  };
}
